import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  PostV1TrackingEventsApiArg,
  usePostV1TrackingEventsMutation,
} from '@/redux/apis/frontend-api';
import { getSessionId } from '@/lib/session';
import { DISABLE_TRACKING } from '../env';

type Event = PostV1TrackingEventsApiArg['body'];

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
type JsonObject = { [key: string]: JsonValue };

type TrackContentArgs = JsonObject & { content: string };
type TrackEventArgs = JsonObject & {
  content?: string;
  type: string;
  action: string;
};

const EVENT_DESCRIPTION: Record<Event['event'], string> = {
  PAGE_VIEW: 'User navigation',
  CONTENT_VIEW: 'Content displayed',
  USER_ACTION: 'User action',
};

const eventLogger = ({
  sessionId,
  event,
  data,
}: PostV1TrackingEventsApiArg['body']) =>
  console.info(`[${sessionId}] [${event}] ${EVENT_DESCRIPTION[event]}`, data);

const createEventBody = (event: Event['event'], data: JsonObject): Event => {
  const searchParams = new URLSearchParams(window.location.search);
  const queryParams = Object.fromEntries(searchParams) as Record<
    string,
    string
  >;
  const path = window.location.pathname;
  const sessionId = getSessionId();

  return {
    event,
    sessionId,
    data: {
      ...data,
      path,
      queryParams,
    },
  };
};

export const useTrackRoute = () => {
  const { pathname: path } = useLocation();
  const [trackEventMutation] = usePostV1TrackingEventsMutation();

  useEffect(() => {
    const body = createEventBody('PAGE_VIEW', {});

    eventLogger(body);

    if (!DISABLE_TRACKING) {
      trackEventMutation({
        body,
      });
    }
  }, [path, trackEventMutation, DISABLE_TRACKING]);
};

export const useTrackContent = () => {
  const [trackEventMutation] = usePostV1TrackingEventsMutation();
  const dispatch = useCallback(
    async (extra: TrackContentArgs) => {
      const body = createEventBody('CONTENT_VIEW', extra);

      eventLogger(body);

      if (!DISABLE_TRACKING) {
        await trackEventMutation({
          body,
        });
      }
    },
    [trackEventMutation, DISABLE_TRACKING]
  );

  return [dispatch] as const;
};

export const useTrackInteraction = () => {
  const [trackEventMutation] = usePostV1TrackingEventsMutation();
  const dispatch = useCallback(
    async (extra: TrackEventArgs) => {
      const body = createEventBody('USER_ACTION', extra);

      eventLogger(body);

      if (!DISABLE_TRACKING) {
        await trackEventMutation({
          body,
        });
      }
    },
    [trackEventMutation, DISABLE_TRACKING]
  );

  return [dispatch] as const;
};
